import type { PostData } from "@/services/api";

export function getSingleReceiverId(
  post: Pick<PostData, "receiverID" | "receiverIDs">,
): string | undefined {
  if (post.receiverIDs && post.receiverIDs.length > 1) return undefined;
  if (post.receiverID) return post.receiverID;
  if (post.receiverIDs && post.receiverIDs.length === 1)
    return post.receiverIDs[0];
  return undefined;
}

/** Direct 1:1 joydrop (not community, not send-all): both parties control visibility. */
export function isDualPrivacyJoydrop(
  post: Pick<PostData, "communityId" | "receiverID" | "receiverIDs">,
): boolean {
  if (post.communityId) return false;
  if (post.receiverIDs && post.receiverIDs.length > 1) return false;
  return !!getSingleReceiverId(post);
}

/** This user's preference: true = wants public. Falls back to effective `isPublic` for legacy posts. */
export function getMyWantsPublic(
  post: PostData,
  userId: string | undefined,
): boolean {
  if (!userId) return post.isPublic;
  if (userId === post.senderID) {
    return post.senderWantsPublic ?? post.isPublic;
  }
  const rid = getSingleReceiverId(post);
  if (rid && userId === rid) {
    return post.receiverWantsPublic ?? post.isPublic;
  }
  return post.isPublic;
}

/** Menu state for the non-community post ⋮ visibility action. */
export function getJoydropVisibilityMenuState(
  post: PostData | undefined,
  userId: string | undefined,
): { canChange: boolean; myWantsPublic: boolean } {
  if (!post || !userId) {
    return { canChange: false, myWantsPublic: true };
  }
  if (post.communityId) {
    return { canChange: false, myWantsPublic: true };
  }
  if (isDualPrivacyJoydrop(post)) {
    const rid = getSingleReceiverId(post);
    const isParty =
      userId === post.senderID || (!!rid && userId === rid);
    return {
      canChange: isParty,
      myWantsPublic: getMyWantsPublic(post, userId),
    };
  }
  return {
    canChange: userId === post.senderID,
    myWantsPublic: getMyWantsPublic(post, userId),
  };
}
